import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Bell } from "lucide-solid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@mealplan/shared";
import { useI18n } from "@/providers/i18n-provider";

type Resp = components["schemas"]["NotificationPreferencesResponse"];

const CHANNELS = ["email", "push", "in_app"] as const;
const CATEGORIES = ["marketing", "product"] as const; // transactional no es opt-out-able

/**
 * Panel de preferencias de notificación del usuario (campaña #11). Permite
 * habilitar/deshabilitar cada canal × categoría. El transaccional de seguridad
 * no se muestra porque siempre se envía (verificación/reset).
 */
export function NotificationPreferencesSection() {
  const { t } = useI18n();

  const prefsQuery = createApiQuery<Resp, components["schemas"]["ErrorModel"]>(
    () => ["notifications", "preferences"],
    async () => api.GET("/api/notifications/preferences"),
    () => ({ staleTime: 30_000 }),
  );

  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["UpdatePreferenceDto"]) =>
      api.POST("/api/notifications/preferences", { body: vars }),
  );

  const prefMap = createMemo(() => {
    const map = new Map<string, boolean>();
    for (const p of prefsQuery.data?.preferences ?? []) {
      map.set(`${p.channel}:${p.category}`, p.enabled);
    }
    return map;
  });

  const isEnabled = (channel: string, category: string) =>
    prefMap().get(`${channel}:${category}`) ?? true;

  const toggle = async (channel: string, category: string, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ channel, category, enabled });
      await prefsQuery.refetch();
    } catch (err) {
      console.error("update preference error", err);
    }
  };

  const channelLabel = (c: string) =>
    c === "email" ? t(msg`Email`) : c === "push" ? t(msg`Push`) : t(msg`In-app`);
  const categoryLabel = (c: string) =>
    c === "marketing" ? t(msg`Marketing & newsletters`) : t(msg`Product updates`);

  return (
    <Card>
      <CardHeader>
        <CardTitle class="flex items-center gap-2">
          <Bell class="h-5 w-5 text-primary" />
          {t(msg`Notification preferences`)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Show when={!prefsQuery.isLoading} fallback={<Skeleton class="h-32 w-full rounded-xl" />}>
          <div class="space-y-6">
            <For each={CATEGORIES}>
              {(category) => (
                <div>
                  <p class="mb-2 text-sm font-medium">{categoryLabel(category)}</p>
                  <div class="space-y-3">
                    <For each={CHANNELS}>
                      {(channel) => (
                        <div class="flex items-center justify-between">
                          <span class="text-sm text-muted-foreground">{channelLabel(channel)}</span>
                          <Switch
                            checked={isEnabled(channel, category)}
                            onCheckedChange={(v) => toggle(channel, category, v)}
                          />
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Security emails (verification, password reset) are always sent and can't be disabled.`,
              )}
            </p>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
